import { useCallback, useReducer } from "react";

export const PANEL_DEFAULT_WIDTH_PX = 480;

interface ChatPanelState {
  inputText: string;
  isAwaitingAgentResponse: boolean;
  panelWidthInPixels: number;
  isClearConversationModalOpen: boolean;
  clearedMessageCount: number;
  view: "chat" | "draft";
  isDraftResultView: boolean;
  backToSelectionToken: number;
}

type ChatPanelAction =
  | { type: "setInputText"; inputText: string }
  | { type: "setIsAwaitingAgentResponse"; isAwaitingAgentResponse: boolean }
  | { type: "setPanelWidthInPixels"; panelWidthInPixels: number }
  | { type: "openClearConversationModal" }
  | { type: "closeClearConversationModal" }
  | { type: "confirmClearConversation"; messageCountAtClear: number }
  | { type: "openDraftPanel" }
  | { type: "closeDraftPanel" }
  | { type: "setIsDraftResultView"; isDraftResultView: boolean }
  | { type: "requestBackToSelection" };

const chatPanelInitialState: ChatPanelState = {
  inputText: "",
  isAwaitingAgentResponse: false,
  panelWidthInPixels: PANEL_DEFAULT_WIDTH_PX,
  isClearConversationModalOpen: false,
  clearedMessageCount: 0,
  view: "chat",
  isDraftResultView: false,
  backToSelectionToken: 0,
};

const chatPanelReducer = (
  state: ChatPanelState,
  action: ChatPanelAction,
): ChatPanelState => {
  switch (action.type) {
    case "setInputText":
      return { ...state, inputText: action.inputText };
    case "setIsAwaitingAgentResponse":
      return { ...state, isAwaitingAgentResponse: action.isAwaitingAgentResponse };
    case "setPanelWidthInPixels":
      return { ...state, panelWidthInPixels: action.panelWidthInPixels };
    case "openClearConversationModal":
      return { ...state, isClearConversationModalOpen: true };
    case "closeClearConversationModal":
      return { ...state, isClearConversationModalOpen: false };
    case "confirmClearConversation":
      return {
        ...state,
        inputText: "",
        isAwaitingAgentResponse: false,
        isClearConversationModalOpen: false,
        clearedMessageCount: action.messageCountAtClear,
      };
    case "openDraftPanel":
      return { ...state, view: "draft", isDraftResultView: false };
    case "closeDraftPanel":
      return { ...state, view: "chat", isDraftResultView: false };
    case "setIsDraftResultView":
      return { ...state, isDraftResultView: action.isDraftResultView };
    case "requestBackToSelection":
      return { ...state, backToSelectionToken: state.backToSelectionToken + 1 };
    default:
      return state;
  }
};

export const useChatPanelState = () => {
  const [state, dispatch] = useReducer(chatPanelReducer, chatPanelInitialState);

  const setInputText = useCallback(
    (inputText: string) => dispatch({ type: "setInputText", inputText }),
    [],
  );
  const setIsAwaitingAgentResponse = useCallback(
    (isAwaitingAgentResponse: boolean) =>
      dispatch({ type: "setIsAwaitingAgentResponse", isAwaitingAgentResponse }),
    [],
  );
  const setPanelWidthInPixels = useCallback(
    (panelWidthInPixels: number) =>
      dispatch({ type: "setPanelWidthInPixels", panelWidthInPixels }),
    [],
  );
  const openClearConversationModal = useCallback(
    () => dispatch({ type: "openClearConversationModal" }),
    [],
  );
  const closeClearConversationModal = useCallback(
    () => dispatch({ type: "closeClearConversationModal" }),
    [],
  );
  const confirmClearConversation = useCallback(
    (messageCountAtClear: number) =>
      dispatch({ type: "confirmClearConversation", messageCountAtClear }),
    [],
  );
  const openDraftPanel = useCallback(() => dispatch({ type: "openDraftPanel" }), []);
  const closeDraftPanel = useCallback(() => dispatch({ type: "closeDraftPanel" }), []);
  const setIsDraftResultView = useCallback(
    (isDraftResultView: boolean) =>
      dispatch({ type: "setIsDraftResultView", isDraftResultView }),
    [],
  );
  const requestBackToSelection = useCallback(
    () => dispatch({ type: "requestBackToSelection" }),
    [],
  );

  return {
    ...state,
    setInputText,
    setIsAwaitingAgentResponse,
    setPanelWidthInPixels,
    openClearConversationModal,
    closeClearConversationModal,
    confirmClearConversation,
    openDraftPanel,
    closeDraftPanel,
    setIsDraftResultView,
    requestBackToSelection,
  };
};
